import { BreakpointObserver } from '@angular/cdk/layout';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Component, inject, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import {
    AuthService,
    Block,
    BlockService,
    ConfigLoginPage,
    ConfigService,
    PageService,
    PageType,
    scrollToTop,
    SettingsService,
    SpinnerService,
} from '@gallaghercp/core-frontend';
import { Observable, Subscription, tap, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { ClientService } from '../../services/client.service';

@Component({
    selector: 'core-login',
    templateUrl: './login.component.html',
    standalone: false,
})
export class LoginComponent implements OnInit, OnDestroy {
    protected auth = inject(AuthService);
    protected pages = inject(PageService);
    protected api = inject(HttpClient);
    protected spinner = inject(SpinnerService);
    client = inject(ClientService);
    protected settings = inject(SettingsService);
    protected router = inject(Router);
    protected route = inject(ActivatedRoute);
    protected titleService = inject(Title);
    protected breakpoint = inject(BreakpointObserver);
    protected config = inject(ConfigService);
    private platformId = inject(PLATFORM_ID);

    readonly loginPageConfig?: ConfigLoginPage = this.config.get('loginPage');

    get hasHeader(): boolean {
        return !!this.loginPageConfig?.includeHeader;
    }

    get hasFooter(): boolean {
        return !!this.loginPageConfig?.includeFooter;
    }

    get hasCustomTheme(): boolean {
        return !!(
            this.loginPageConfig?.pageBackgroundColor ||
            this.loginPageConfig?.pageBackgroundImage ||
            this.loginPageConfig?.formImage ||
            (this.loginPageConfig as any)?.customThemePageBackgroundImage
        );
    }

    get pageBackgroundStyles(): Record<string, string> {
        const styles: Record<string, string> = {};
        const config = this.loginPageConfig as any;
        if (this.loginPageConfig?.pageBackgroundColor) {
            styles['background-color'] = this.loginPageConfig.pageBackgroundColor;
        }
        if (this.loginPageConfig?.pageBackgroundImage) {
            styles['background-image'] = `url(${this.loginPageConfig.pageBackgroundImage})`;
            styles['background-repeat'] = 'no-repeat';
            styles['background-size'] = 'cover';
            styles['background-position'] = 'center';
        }
        if (config?.customThemePageBackgroundImage) {
            styles['background-image'] = `url(${config.customThemePageBackgroundImage})`;
            styles['background-repeat'] = 'no-repeat';
            styles['background-size'] = 'cover';
            styles['background-position'] = 'center';
        }
        return styles;
    }

    get formImage(): string | undefined {
        return this.loginPageConfig?.formImage || undefined;
    }

    get formImagePlacement(): 'left' | 'right' | 'top' | 'bottom' {
        return this.loginPageConfig?.formImagePlacement || 'left';
    }

    get titleText(): string {
        return this.loginPageConfig?.title || this.client.getLoginTitle();
    }

    get introHtml(): string {
        return this.loginPageConfig?.introText || this.client.getLoginWelcomeText();
    }

    get contactHtml(): string | undefined {
        return this.loginPageConfig?.contactText || undefined;
    }

    get contactTitle(): string | undefined {
        return (this.loginPageConfig as any)?.contactTitle || undefined;
    }

    get cardBorderRadius(): string | boolean | undefined {
        return (this.loginPageConfig as any)?.cardBorderRadius;
    }

    get cardButtonBorderRadius(): string | undefined {
        return (this.loginPageConfig as any)?.cardButtonBorderRadius;
    }

    get passwordInputBorderRadius(): string | undefined {
        return (this.loginPageConfig as any)?.passwordInputBorderRadius;
    }

    get buttonFullWidth(): boolean {
        return (this.loginPageConfig as any)?.buttonFullWidth ?? true;
    }

    get buttonAlign(): 'left' | 'right' {
        return (this.loginPageConfig as any)?.buttonAlign || 'right';
    }

    get disableButtonDisabledStyles(): boolean {
        return (this.loginPageConfig as any)?.disableButtonDisabledStyles ?? false;
    }

    get loginButtonClasses(): Record<string, boolean> {
        return {
            btn: true,
            'btn-primary': true,
            'w-100': this.buttonFullWidth,
            'btn--login': true,
            'float-end': this.buttonAlign === 'right',
            'float-start': this.buttonAlign === 'left',
        };
    }

    get cardContainerClasses(): Record<string, boolean> {
        return {
            'login__card-container': true,
            'login__card-container--split': !!this.formImage && this.formImagePlacement === 'left',
            'login__card-container--split--reverse': !!this.formImage && this.formImagePlacement === 'right',
            'login__card-container--split--bottom': !!this.formImage && this.formImagePlacement === 'bottom',
            'login__card-container--stacked-top': !!this.formImage && this.formImagePlacement === 'top',
        };
    }

    get cardStyles(): Record<string, string> {
        const styles: Record<string, string> = {};
        const config = this.loginPageConfig as any;

        if (config?.cardBorderColor) {
            styles['border'] = `1px solid ${config.cardBorderColor}`;
        }

        if (config?.cardBackgroundColor) {
            styles['background-color'] = config.cardBackgroundColor;
        }

        const propertyMap: Record<string, string> = {
            cardTitleColor: '--login-card-title-color',
            cardIntroColor: '--login-card-intro-color',
            cardLabelColor: '--login-card-label-color',
            cardContactColor: '--login-card-contact-color',
            cardContactTextColor: '--login-card-contact-text-color',
            cardButtonTextColor: '--login-card-button-text-color',
            cardIntroFontSize: '--login-card-intro-font-size',
            cardContactTitleColor: '--login-card-contact-title-color',
            cardContactTitleFontSize: '--login-card-contact-title-font-size',
            passwordInputBorderColor: '--login-password-input-border-color',
            loginButtonTextAlign: '--login-button-text-align',
        };

        Object.keys(propertyMap).forEach(key => {
            if (config?.[key]) {
                styles[propertyMap[key]] = config[key];
            }
        });

        if (config?.cardContactColor && isPlatformBrowser(this.platformId)) {
            const bodyStyles = getComputedStyle(document.body);
            const checkColors = ['--link-text', '--color-primary'];

            const hasConflict = checkColors.some(prop => {
                const value = bodyStyles.getPropertyValue(prop).trim();
                return value && value === config.cardBackgroundColor;
            });

            if (hasConflict) {
                styles['--login-link-text-override'] = config.cardContactColor;
            }
        }

        if (this.cardButtonBorderRadius) {
            styles['--login-card-button-border-radius'] = this.cardButtonBorderRadius;
        }

        if (this.cardBorderRadius !== undefined && this.cardBorderRadius !== null) {
            styles['--login-card-border-radius'] = String(this.cardBorderRadius);
        }

        if (this.passwordInputBorderRadius) {
            styles['--login-password-input-border-radius'] = this.passwordInputBorderRadius;
        }

        return styles;
    }

    copyright: string = this.config.get('copyright') || '';
    appName: string = this.config.get('appName') || '';
    loginBulletin?: { text?: string; linkHref?: string; linkText?: string } = this.config.get('loginBulletin');

    loading: boolean = false;
    errorMessage?: string | boolean;
    successMessage?: string | boolean;
    loginType?: string;
    passwordType = 'password';
    subscriptions: Subscription[] = [];
    darkMode?: boolean;
    managedPage$?: Observable<PageType>;
    loginForm = new UntypedFormGroup({
        username: new UntypedFormControl('', [Validators.required, Validators.email]),
        password: new UntypedFormControl('', [Validators.required, Validators.minLength(5)]),
    });

    ngOnInit() {
        this.client.hideSessionModals();
        this.subscriptions.push(
            this.settings.isDarkMode().subscribe((state: any) => {
                this.darkMode = state;
            }),
        );
        this.subscriptions.push(
            this.settings.isDarkModeEnabled().subscribe((state: any) => {
                if (state === false && this.darkMode) {
                    this.settings.toggle('isDarkMode');
                }
            }),
        );

        this.titleService.setTitle('Login - ' + this.appName);

        this.subscriptions.push(
            this.auth
                .validateAuthentication()
                .pipe(
                    switchMap(() => {
                        if (this.auth.isAuthenticated()) {
                            this.router.navigate(['/']);
                        }
                        return this.client.getAccessType();
                    }),
                    tap((type: any) => {
                        this.loginType = type;
                        if (this.loginType === 'public' && this.router.url === '/login') {
                            this.router.navigate(['/']);
                        }
                        if (this.loginType === 'generic' || this.loginType === 'generic-with-username') {
                            this.loginForm.get('username')!.setValidators([]);
                        }
                        if (this.loginType === 'individual-managed') {
                            this.managedPage$ = this.pages.getPage('login');
                        }
                    }),
                    switchMap(() => this.auth.initialise()),
                )
                .subscribe(),
        );
    }

    togglePasswordType() {
        this.passwordType = this.passwordType === 'text' ? 'password' : 'text';
    }

    login() {
        if (!this.loginForm.valid) {
            this.loginForm.markAllAsTouched();
            return;
        }
        this.loading = true;
        this.auth.clearAuthMessaging();
        this.errorMessage = false;
        this.successMessage = false;
        this.spinner.show('login');
        return this.auth
            .login(this.loginForm.get('username')!.value, this.loginForm.get('password')!.value)
            .pipe(
                catchError((error: HttpErrorResponse) => {
                    scrollToTop(500);
                    this.spinner.hide('login');
                    this.loading = false;
                    if (
                        error.status > 0 &&
                        error.status !== 418 &&
                        error.status !== 419 &&
                        error.status !== 420 &&
                        error.status !== 421
                    ) {
                        this.errorMessage =
                            this.loginType === 'generic' || this.loginType === 'generic-with-username'
                                ? `<p>Please check the password is correct, and try again.</p>
                                    <p>Error reference: ${error.headers.get('x-service-request-id')}</small></p>`
                                : `<p>Please check your email address and password are correct, and try again.</p>
                                    <p>Error reference: ${error.headers.get('x-service-request-id')}</small></p>`;
                        this.auth.setAuthMessaging('error', this.errorMessage);
                    }
                    return throwError(() => {
                        return `Error Code: ${error.status}\nMessage: ${error.message}`;
                    });
                }),
            )
            .subscribe(() => {
                this.spinner.show('login');
                this.loading = true;
            });
    }

    getSiteName() {
        return this.client.getSiteName();
    }

    getManagedPage$(): Observable<PageType> | undefined {
        return this.pages.getPage('login');
    }

    trackByFn(idx: number, item: any) {
        return item.uid + '_' + idx;
    }

    getBlockClassPosition(block: Block, previousBlock?: Block) {
        return BlockService.getBlockClassPosition(block, previousBlock);
    }

    getBlockClasses(block: Block) {
        return BlockService.getBlockClasses(block);
    }

    ngOnDestroy() {
        this.subscriptions.forEach(subscription => {
            subscription.unsubscribe();
        });
    }
}
